// The admin REST client: every call carries the signed-in admin's Bearer access token and hits
// `${authority}admin/...`. Behind `IAdminApi` so the ViewModels are tested against `FakeAdminApi` (no network).
// Errors map to tagged DomainErrors so the ViewModels can branch on them (403 -> a clear "not an admin").

import { token } from '@/infrastructure/container';
import { type IAuth } from '@/services/AuthService';
import { ConflictError, NotFoundError, ServerError, ValidationError } from '@/domain/errors';
import { type AdminClient, type AdminSession, type AdminUser, type AuditEvent, type ClientSpec, type EventsQuery } from '@/domain/admin';

export interface CreatedClient {
  client_id: string;
  public: boolean;
  secret?: string; // returned ONCE for a confidential client
}

export interface IAdminApi {
  listUsers(): Promise<AdminUser[]>;
  getUser(username: string): Promise<AdminUser>;
  assignRole(username: string, role: string): Promise<void>;
  revokeRole(username: string, role: string): Promise<void>;
  setEnabled(username: string, enabled: boolean): Promise<void>;
  listUserSessions(username: string): Promise<AdminSession[]>;
  logoutAllUser(username: string): Promise<void>;
  queryEvents(q: EventsQuery): Promise<AuditEvent[]>;
  listClients(): Promise<AdminClient[]>;
  getClient(clientId: string): Promise<AdminClient>;
  createClient(clientId: string, spec: ClientSpec, isPublic: boolean): Promise<CreatedClient>;
  updateClient(clientId: string, spec: ClientSpec): Promise<void>;
  rotateSecret(clientId: string): Promise<string>; // the new plaintext secret (once)
  deleteClient(clientId: string): Promise<void>;
}

export const TOKEN_IAdminApi = token<IAdminApi>('IAdminApi');

export class AdminApi implements IAdminApi {
  constructor(private auth: IAuth) {}

  private base(): string {
    // authority is `${host}/auth/v1/` (trailing slash) -> admin endpoints live under `admin/`.
    return `${this.auth.config.authority}admin`;
  }

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const user = await this.auth.currentUser();
    if (!user) throw new ServerError('not signed in');
    const res = await fetch(`${this.base()}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${user.accessToken}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    if (!res.ok) {
      const detail = text.slice(0, 300);
      if (res.status === 404) throw new NotFoundError(detail || path);
      if (res.status === 409) throw new ConflictError(detail || 'conflict');
      if (res.status === 400) throw new ValidationError('request', detail || 'bad request');
      if (res.status === 403) throw new ServerError('forbidden: the signed-in user is not an admin');
      throw new ServerError(`${res.status} ${res.statusText}: ${detail}`);
    }
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  async listUsers(): Promise<AdminUser[]> {
    const r = await this.req<{ users: AdminUser[] }>('GET', '/users');
    return r.users;
  }
  async getUser(username: string): Promise<AdminUser> {
    return this.req<AdminUser>('GET', `/users/${encodeURIComponent(username)}`);
  }
  async assignRole(username: string, role: string): Promise<void> {
    await this.req('POST', `/users/${encodeURIComponent(username)}/roles`, { role });
  }
  async revokeRole(username: string, role: string): Promise<void> {
    await this.req('DELETE', `/users/${encodeURIComponent(username)}/roles/${encodeURIComponent(role)}`);
  }
  async setEnabled(username: string, enabled: boolean): Promise<void> {
    await this.req('POST', `/users/${encodeURIComponent(username)}/${enabled ? 'enable' : 'disable'}`);
  }
  async listUserSessions(username: string): Promise<AdminSession[]> {
    const r = await this.req<{ sessions: AdminSession[] }>('GET', `/users/${encodeURIComponent(username)}/sessions`);
    return r.sessions;
  }
  async logoutAllUser(username: string): Promise<void> {
    await this.req('POST', `/users/${encodeURIComponent(username)}/logout-all`);
  }
  async queryEvents(q: EventsQuery): Promise<AuditEvent[]> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
    }
    const qs = params.toString();
    const r = await this.req<{ events: AuditEvent[] }>('GET', `/events${qs ? `?${qs}` : ''}`);
    return r.events;
  }
  async listClients(): Promise<AdminClient[]> {
    const r = await this.req<{ clients: AdminClient[] }>('GET', '/clients');
    return r.clients;
  }
  async getClient(clientId: string): Promise<AdminClient> {
    return this.req<AdminClient>('GET', `/clients/${encodeURIComponent(clientId)}`);
  }
  async createClient(clientId: string, spec: ClientSpec, isPublic: boolean): Promise<CreatedClient> {
    return this.req<CreatedClient>('POST', '/clients', { client_id: clientId, public: isPublic, ...spec });
  }
  async updateClient(clientId: string, spec: ClientSpec): Promise<void> {
    await this.req('PUT', `/clients/${encodeURIComponent(clientId)}`, spec);
  }
  async rotateSecret(clientId: string): Promise<string> {
    const r = await this.req<{ secret: string }>('POST', `/clients/${encodeURIComponent(clientId)}/rotate-secret`);
    return r.secret;
  }
  async deleteClient(clientId: string): Promise<void> {
    await this.req('DELETE', `/clients/${encodeURIComponent(clientId)}`);
  }
}

// A stateful in-memory fake for ViewModel tests: holds users/clients/events and applies mutations, so a store
// test can assert "disable then the user shows disabled" without a server. `failNext` injects one error.
export class FakeAdminApi implements IAdminApi {
  users: AdminUser[] = [];
  clients: AdminClient[] = [];
  events: AuditEvent[] = [];
  sessions: Record<string, AdminSession[]> = {};
  failNext: Error | null = null;
  lastCreatedSecret = 'fake-secret-AAAA';

  private maybeThrow(): void {
    if (this.failNext) {
      const e = this.failNext;
      this.failNext = null;
      throw e;
    }
  }
  private user(username: string): AdminUser {
    const u = this.users.find((x) => x.username === username);
    if (!u) throw new NotFoundError(username);
    return u;
  }

  async listUsers(): Promise<AdminUser[]> {
    this.maybeThrow();
    return [...this.users];
  }
  async getUser(username: string): Promise<AdminUser> {
    this.maybeThrow();
    return { ...this.user(username) };
  }
  async assignRole(username: string, role: string): Promise<void> {
    this.maybeThrow();
    const u = this.user(username);
    if (!u.roles.includes(role)) u.roles.push(role);
  }
  async revokeRole(username: string, role: string): Promise<void> {
    this.maybeThrow();
    const u = this.user(username);
    u.roles = u.roles.filter((r) => r !== role);
  }
  async setEnabled(username: string, enabled: boolean): Promise<void> {
    this.maybeThrow();
    this.user(username).disabled = !enabled;
  }
  async listUserSessions(username: string): Promise<AdminSession[]> {
    this.maybeThrow();
    return this.sessions[username] ?? [];
  }
  async logoutAllUser(username: string): Promise<void> {
    this.maybeThrow();
    this.sessions[username] = [];
  }
  async queryEvents(_q: EventsQuery): Promise<AuditEvent[]> {
    this.maybeThrow();
    return [...this.events];
  }
  async listClients(): Promise<AdminClient[]> {
    this.maybeThrow();
    return [...this.clients];
  }
  async getClient(clientId: string): Promise<AdminClient> {
    this.maybeThrow();
    const c = this.clients.find((x) => x.client_id === clientId);
    if (!c) throw new NotFoundError(clientId);
    return { ...c };
  }
  async createClient(clientId: string, spec: ClientSpec, isPublic: boolean): Promise<CreatedClient> {
    this.maybeThrow();
    if (this.clients.some((c) => c.client_id === clientId)) throw new ConflictError('exists');
    this.clients.push({ client_id: clientId, public: isPublic, ...spec });
    return { client_id: clientId, public: isPublic, ...(isPublic ? {} : { secret: this.lastCreatedSecret }) };
  }
  async updateClient(clientId: string, spec: ClientSpec): Promise<void> {
    this.maybeThrow();
    const c = this.clients.find((x) => x.client_id === clientId);
    if (!c) throw new NotFoundError(clientId);
    Object.assign(c, spec);
  }
  async rotateSecret(clientId: string): Promise<string> {
    this.maybeThrow();
    if (!this.clients.some((c) => c.client_id === clientId)) throw new NotFoundError(clientId);
    return 'rotated-secret-BBBB';
  }
  async deleteClient(clientId: string): Promise<void> {
    this.maybeThrow();
    this.clients = this.clients.filter((c) => c.client_id !== clientId);
  }
}
